import React, { useRef, useState, ChangeEvent } from "react";
import { Avatar, Box, IconButton, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Custom } from "components";

// icons
import { UploadRounded } from "@mui/icons-material";
import { useApp, useTheme } from "contexts";
import { MessageType } from "types";
import { Enums, S3 } from "utils";

const Overlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  opacity: 0,
  transition: "opacity 0.3s",
  cursor: "pointer",
  borderRadius: "50%",
}));

const Container = styled(Box)({
  position: "relative",
  width: 120,
  height: 120,
  "&:hover": {
    "& .overlay": {
      opacity: 1,
    },
  },
});

type Props = {
  src: string;
  alt: string;
};

export const UploadableAvatar = (props: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { setFeedback, t } = useApp();
  const { theme } = useTheme();

  const [image, setImage] = useState<string | null>(null);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);

        file["newName"] = [props.src, "png"].join(".");

        S3.upload("avatars", [file]);

        setFeedback({
          severity: Enums.EnumFeedback.Success,
          message: t.message.feedback.request_update_success,
        } as MessageType);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Stack spacing={theme.spacing.sm} alignItems="center">
      <Container>
        <Custom.Avatar
          size="x-large"
          alt={props.alt}
          src={image || props.src}
          sx={{ width: 1, height: 1 }}
        />
        <Overlay className="overlay" onClick={handleClick}>
          <Custom.IconButton size="large" foreground={theme.color.white}>
            <UploadRounded />
          </Custom.IconButton>
        </Overlay>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileChange}
        />
      </Container>
      <Custom.Typography
        size={theme.font.sm}
        sx={{ cursor: "pointer" }}
        weight={theme.font.medium}
        color={theme.color.accent.color}
        onClick={() => inputRef.current?.click()}
      >
        {t.component.uploadable_avatar}
      </Custom.Typography>
    </Stack>
  );
};
